import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../../Reducer/SnackbarReducer';
import Commons from '../../../../../Services/Common/Common';
import { useSave } from '../../../../../hooks/useSaveN';
import { mapParamsBySchema } from '../../../../../util';
import { apiRequest } from '../../../../../Services/Common/ApiRequest';
import Global from '../../../../../Services/Common/Global';
import SafetyInfo from './SafetyInfo';
import {
  ExtractGetRisCustom01R4RiskDetailList,
  GetProcessDisplayName,
  GetSafetyInfoDefaultForm,
  MapGetRisCustom01R4ResponseToForm,
  MapGetRisCustom01R1ResponseToForm,
  MapRiskMasterRowToSafetyInfoBasicEval,
  MapGetRisCustom01R2ResponseToProcessModal,
  MapOtherEquipmentToFacilityRows,
  MapOtherMaterialToRows,
  MANUAL_MATERIAL_PRODUCT_ID,
  NormalizeDepCdKey,
  ProcessesMatchForSelection,
  SafetyInfo_Schema,
  SAFETY_INFO_FACILITY_CATEGORIES,
  SAFETY_INFO_FACILITY_CATEGORIES_WITH_OTHER,
} from './SafetyInfo_Schema';
import {
  useSave1,
  useSave2,
  useSave3,
  useSave4,
  useSearch1,
  useSearch2,
  useSearch3,
  useDelete1,
  useDelete2,
} from './SafetyInfo_QUERY';
import ProcessSelectModal from './modals/ProcessSelectModal';
import ProcessRegisterModal from './modals/ProcessRegisterModal';
import ItemSelectModal from './modals/ItemSelectModal';
import RiskAssessmentListModal from './modals/RiskAssessmentListModal';
import useDragDropReorder from '../../RIS_BASIC_01/hooks/useDragDropReorder';
import useItemSelection, {
  FACILITY_OTHER_CATEGORY_VALUE,
} from './hooks/useItemSelection';
import RIS_BASIC_01_Schema from '../../RIS_BASIC_01/RIS_BASIC_01_Schema';
import { RIS_CUSTOM_Schema } from '../RIS_CUSTOM_Schema';
import { useWaitDashboard1 } from '../Step00_Wait/Wait_QUERY';
import { useRisCustomWorkflow } from '../RisCustomWorkflowContext';
import {
  MapGetRisCustom02R1ToProcesses,
  MergeSafetyProcessListWithRiskDetails,
  StripCopiedRiskDetailRowsForDraft,
} from '../RiskAssessment/RiskAssessment_Schema';

const SCREEN_MENU_ID = 'RiskEvaluation';

function SafetyInfoF({
  assessmentRsCd = null,
  assessmentTitle = '',
  onCancel,
  onSaved,
}) {
  const risWorkflow = useRisCustomWorkflow();
  const dispatch = useDispatch();
  const searchInfo = useSelector((s) => s.SearchInfo);
  const [form, setForm] = useState(GetSafetyInfoDefaultForm());

  const [depData, setDepData] = useState([]);
  const [processDataByDept, setProcessDataByDept] = useState({});
  const [processSelectModalOpen, setProcessSelectModalOpen] = useState(false);
  const [processRegisterModalOpen, setProcessRegisterModalOpen] = useState(false);
  const [processRegisterData, setProcessRegisterData] = useState({
    depCd: '',
    gongJongNm: '',
  });
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedProcessCodes, setSelectedProcessCodes] = useState([]);
  const [departmentSearchText, setDepartmentSearchText] = useState('');
  const [processSearchText, setProcessSearchText] = useState('');

  const [facilityDataByCategory, setFacilityDataByCategory] = useState({
    0: [],
    1: [],
    2: [],
    [FACILITY_OTHER_CATEGORY_VALUE]: [],
  });
  const [materialProducts, setMaterialProducts] = useState([]);
  const [materialDataByProduct, setMaterialDataByProduct] = useState({});

  /** 설비·물질 공통 ItemSelectModal */
  const [itemSelectModalOpen, setItemSelectModalOpen] = useState(false);
  const [itemSelectModalType, setItemSelectModalType] = useState('facility');
  const [riskListModalOpen, setRiskListModalOpen] = useState(false);
  const [riskListRows, setRiskListRows] = useState([]);
  const [riskListSearchText, setRiskListSearchText] = useState('');
  const riskListGridRef = useRef(null);
  /** 물질 모달: Search3 재조회마다 폼만으로 덮어쓰지 않도록, 모달 세션당 하이드레이트 1회만 */
  const materialModalHydratedRef = useRef(false);

  const {
    data: search1Data,
    isError: search1IsError,
    error: search1Error,
  } = useSearch1({
    rsCd: assessmentRsCd,
    enabled: Number(assessmentRsCd) > 0,
  });
  const { data: waitDashboardData } = useWaitDashboard1({
    custCd: searchInfo?.custCd,
    rsCd: assessmentRsCd,
    titleOverride: assessmentTitle,
    enabled: Number(assessmentRsCd) > 0,
  });

  /** GetRisCustom00R1 대시보드 스텝만 사용 (정적 스텝 목록 없음) */
  const workflowStepsFromApi = useMemo(() => {
    const live = waitDashboardData?.steps;
    if (!Array.isArray(live) || live.length === 0) return [];
    return live.map((s, i) => {
      const id = s.routeStepId || s.stepId || `step-${i}`;
      return {
        stepId: id,
        routeStepId: s.routeStepId || s.stepId,
        title: String(s.title ?? s.stepNm ?? `단계 ${i + 1}`),
        status: s.status,
      };
    });
  }, [waitDashboardData?.steps]);

  const { run: Search2 } = useSearch2({
    onSuccess: (_rows, response) => {
      const { depData, processDataByDept } =
        MapGetRisCustom01R2ResponseToProcessModal(response);
      setDepData(Commons.InsertNumber(depData) || []);
      setProcessDataByDept(processDataByDept);
    },
  });

  const { run: Search3 } = useSearch3({
    onSuccess: (rows) => {
      if (!rows || rows.length === 0) return;
      const data = rows[0];
      const otherRaw =
        data.otherEquipment ?? data.OtherEquipment ?? [];
      setFacilityDataByCategory({
        0: data.hazBasic01R2 ?? data.HazBasic01R2 ?? [],
        1: data.hazBasic02R2 ?? data.HazBasic02R2 ?? [],
        2:
          data.hamBasic02R1 ??
          data.HamBasic02R1 ??
          data.hazBasic03R1 ??
          data.HazBasic03R1 ??
          [],
        [FACILITY_OTHER_CATEGORY_VALUE]:
          MapOtherEquipmentToFacilityRows(otherRaw),
      });
      const rawMaterials =
        data.hamBasic02R1 ?? data.HamBasic02R1 ?? [];
      const otherMaterialRaw =
        data.otherMaterial ?? data.OtherMaterial ?? [];
      const mergedMaterials = [
        ...(Array.isArray(rawMaterials) ? rawMaterials : []),
        ...MapOtherMaterialToRows(otherMaterialRaw),
      ];
      const productMap = new Map();
      const itemsByProduct = {};
      mergedMaterials.forEach((item) => {
        const isManualMaterial = item?.isManual === true;
        const productId = isManualMaterial
          ? MANUAL_MATERIAL_PRODUCT_ID
          : item.hbmId;
        const productName = isManualMaterial
          ? '수기 등록 물질'
          : item.productName || '기타 제품';
        if (!productMap.has(productId)) {
          productMap.set(productId, { value: productId, label: productName });
          itemsByProduct[productId] = [];
        }
        itemsByProduct[productId].push(item);
      });
      if (
        !productMap.has(MANUAL_MATERIAL_PRODUCT_ID) &&
        !productMap.has(String(MANUAL_MATERIAL_PRODUCT_ID))
      ) {
        productMap.set(MANUAL_MATERIAL_PRODUCT_ID, {
          value: MANUAL_MATERIAL_PRODUCT_ID,
          label: '수기 등록 물질',
        });
        itemsByProduct[MANUAL_MATERIAL_PRODUCT_ID] = [];
      }
      setMaterialProducts(Array.from(productMap.values()));
      setMaterialDataByProduct(itemsByProduct);
    },
  });

  const { runAsync: SaveProcessRegisterAsync } = useSave2();
  const { runAsync: SaveSafetyInfoAsync } = useSave1();
  const { runAsync: SaveManualFacilityAsync } = useSave3();
  const { runAsync: DeleteManualFacilityAsync } = useDelete1();
  const { runAsync: SaveManualMaterialAsync } = useSave4();
  const { runAsync: DeleteManualMaterialAsync } = useDelete2();
  const { run: SaveProcessRegister } = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '부서코드,공정명',
    schema: RIS_BASIC_01_Schema,
    getChangedRows: () => [processRegisterData],
    requestSave: (rows) => SaveProcessRegisterAsync(rows),
    afterSaveRow: async () => {
      Search2();
      setProcessRegisterModalOpen(false);
    },
  });

  const facilitySelection = useItemSelection({
    type: 'facility',
    dataSource: {
      categories: SAFETY_INFO_FACILITY_CATEGORIES,
      itemsByCategory: facilityDataByCategory,
    },
    initialState: { selectedCategories: [], selectedItems: [] },
  });

  const materialSelection = useItemSelection({
    type: 'material',
    dataSource: {
      categories: materialProducts,
      itemsByCategory: materialDataByProduct,
    },
    initialState: { selectedCategories: [], selectedItems: [] },
  });

  const materialSelectionRef = useRef(materialSelection);
  materialSelectionRef.current = materialSelection;

  const HydrateMaterialSelectionFromForm = useCallback(() => {
    const pl = Array.isArray(form.processList) ? form.processList : [];
    const sel = pl.find((p) => p.selected) || pl[0];
    const materialList = Array.isArray(sel?.materialList)
      ? sel.materialList
      : Array.isArray(form.materialList)
        ? form.materialList
        : [];
    const asMaterials = materialList.map((m) => ({
      hbdId: m.id,
      materialId: m.id,
      substanceName: m.name,
      materialNm: m.name,
      materialName: m.name,
      hbmId: m.hbmId ?? m.productId,
      productId: m.hbmId ?? m.productId,
      productName: m.productName || '',
      CAS_NO: m.casNo,
      casNo: m.casNo,
    }));
    materialSelectionRef.current.loadFromSavedData(
      asMaterials,
      materialProducts,
      materialDataByProduct
    );
  }, [form.processList, form.materialList, materialProducts, materialDataByProduct]);

  const { handleDragEnd: handleDragEndProcess } = useDragDropReorder(setSelectedProcessCodes);

  /** RS_CD 없음: 샘플/기본 폼 */
  useEffect(() => {
    if (assessmentRsCd && Number(assessmentRsCd) > 0) return;
    const next = GetSafetyInfoDefaultForm();
    if (assessmentTitle) next.rsNm = assessmentTitle;
    setForm(next);
  }, [assessmentRsCd, assessmentTitle]);

  /** RS_CD 변경 시에만 로딩용 빈 껍데기(조회 전 깜빡임 최소화) */
  useEffect(() => {
    if (!assessmentRsCd || Number(assessmentRsCd) <= 0) return;
    setForm((prev) => ({
      ...GetSafetyInfoDefaultForm(),
      rsNm: assessmentTitle || prev.rsNm || '',
      processList: [],
      equipmentList: [],
      materialList: [],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- RS_CD 전환 시에만 초기화
  }, [assessmentRsCd]);

  useEffect(() => {
    if (!search1Data) return;
    const patch = MapGetRisCustom01R1ResponseToForm(search1Data, {
      assessmentTitle,
    });
    if (!patch) return;
    setForm((prev) => ({
      ...prev,
      ...patch,
      rsNm: assessmentTitle || patch.rsNm || prev.rsNm,
    }));
  }, [search1Data, assessmentTitle]);

  /**
   * Step00 실시규정(SaveRisCustomU1)과 동일 소스 — GetRisCustomR1 RiskMasterList로 기본평가정보 바인딩
   */
  useEffect(() => {
    const SynchronizeBasicEvalFromRegulationMaster = async () => {
      if (!assessmentRsCd || Number(assessmentRsCd) <= 0) return;
      try {
        const params = mapParamsBySchema(RIS_CUSTOM_Schema.mapper.search1, {
          search: {
            custCd: searchInfo?.custCd || '',
            startDate: searchInfo?.startDate || '',
            searchText: '',
          },
        });
        const raw = await apiRequest(
          'post',
          Global.urls.RiskEvaluation.getRisCustomR1,
          params
        );
        const first = raw?.Data?.[0] ?? raw?.data?.[0] ?? {};
        const list =
          first?.RiskMasterList ??
          first?.riskMasterList ??
          raw?.RiskMasterList ??
          raw?.riskMasterList ??
          [];
        const target = Array.isArray(list)
          ? list.find(
              (item) =>
                Number(item?.RsCd ?? item?.rsCd) === Number(assessmentRsCd)
            )
          : null;
        if (!target) return;
        const basic = MapRiskMasterRowToSafetyInfoBasicEval(target, {
          assessmentTitle,
        });
        if (!basic) return;

        setForm((prev) => ({
          ...prev,
          ...basic,
          rsNm: basic.rsNm || assessmentTitle || prev.rsNm,
          rsStartDate: basic.rsStartDate || prev.rsStartDate,
          rsEndDate: basic.rsEndDate || prev.rsEndDate,
        }));
      } catch {
        // 마스터 조회 실패는 치명적이지 않음
      }
    };

    SynchronizeBasicEvalFromRegulationMaster();
  }, [
    assessmentRsCd,
    searchInfo?.custCd,
    searchInfo?.startDate,
    assessmentTitle,
  ]);

  /** 조회 후에도 평가명(대기 단계 제목) 동기화 */
  useEffect(() => {
    if (!assessmentRsCd || Number(assessmentRsCd) <= 0 || !assessmentTitle) return;
    setForm((prev) =>
      prev.rsNm === assessmentTitle ? prev : { ...prev, rsNm: assessmentTitle }
    );
  }, [assessmentTitle, assessmentRsCd]);

  useEffect(() => {
    if (!search1IsError || !search1Error) return;
    dispatch(
      showSnackbar({
        message:
          search1Error?.message ||
          '안전보건정보 조회에 실패했습니다.',
        type: 'error',
      })
    );
  }, [search1IsError, search1Error, dispatch]);

  useEffect(() => {
    if (!processSelectModalOpen) return;
    Search2();
    // 모달 오픈 시에만 부서·공정 캐시 갱신 (run 참조는 훅 안정화에 의존)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Search2
  }, [processSelectModalOpen]);

  useEffect(() => {
    Search3();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Search3
  }, []);

  useEffect(() => {
    if (!itemSelectModalOpen) return;
    Search3();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Search3
  }, [itemSelectModalOpen]);

  /**
   * 물질: 모달을 연 직후 제품/물질 맵이 처음 준비될 때만 폼 → 선택 복원.
   * 수기 등록 등으로 Search3가 다시 돌 때마다 호출하면 모달 안에서 고른 항목이 폼 저장분만 남고 초기화됨(설비 팝업과 불일치).
   */
  useEffect(() => {
    if (!itemSelectModalOpen) {
      materialModalHydratedRef.current = false;
      return;
    }
    if (itemSelectModalType !== 'material') return;
    const hasData =
      (Array.isArray(materialProducts) && materialProducts.length > 0) ||
      Object.keys(materialDataByProduct || {}).length > 0;
    if (!hasData) return;
    if (materialModalHydratedRef.current) return;
    HydrateMaterialSelectionFromForm();
    materialModalHydratedRef.current = true;
  }, [
    itemSelectModalOpen,
    itemSelectModalType,
    materialProducts,
    materialDataByProduct,
    HydrateMaterialSelectionFromForm,
  ]);

  const HandleChange = (patch) => {
    setForm((prev) => ({ ...prev, ...(patch ?? {}) }));
  };

  const HandleToggleDepartment = (dep) => {
    const depKey = NormalizeDepCdKey(dep.value);
    setSelectedDepartments((prev) => {
      const exists = prev.some((d) => NormalizeDepCdKey(d.value) === depKey);
      if (exists) {
        return prev.filter((d) => NormalizeDepCdKey(d.value) !== depKey);
      }
      return [...prev, dep];
    });
  };

  const HandleToggleProcessCode = (process) => {
    setSelectedProcessCodes((prev) => {
      const exists = prev.some((p) => ProcessesMatchForSelection(p, process));
      if (exists) {
        return prev.filter((p) => !ProcessesMatchForSelection(p, process));
      }
      return [...prev, process];
    });
  };

  const HandleClearAllProcesses = () => {
    setSelectedProcessCodes([]);
  };

  const HandleOpenProcessSelectModal = () => {
    const processList = Array.isArray(form.processList) ? form.processList : [];
    const allProcesses = Object.values(processDataByDept).flat();
    const existingProcesses = processList
      .filter((p) => p.processId || p.processNm)
      .map((p) => {
        let found = null;
        if (p.processId) {
          found = allProcesses.find((raw) => raw.gongJongCd === p.processId);
        }
        if (!found && p.processNm) {
          found = allProcesses.find((raw) => raw.gongJongNm === p.processNm);
        }
        if (found) return found;
        return {
          gongJongCd: p.processId || '',
          gongJongNm: p.processNm || '',
          depNm: p.depNm || '',
          depCd: p.depCd || '',
        };
      });
    setSelectedProcessCodes(existingProcesses);
    const depCdSet = new Set(
      existingProcesses
        .map((p) => NormalizeDepCdKey(p.depCd))
        .filter((cd) => cd !== '')
    );
    const preChecked =
      depCdSet.size > 0 && Array.isArray(depData)
        ? depData.filter((d) => depCdSet.has(NormalizeDepCdKey(d.value)))
        : [];
    setSelectedDepartments(preChecked);
    setProcessSelectModalOpen(true);
  };

  const HandleCloseProcessSelectModal = () => {
    setProcessSelectModalOpen(false);
    setSelectedDepartments([]);
    setSelectedProcessCodes([]);
    setDepartmentSearchText('');
    setProcessSearchText('');
  };

  const HandleConfirmProcessSelection = () => {
    setForm((prev) => {
      const depLabelByCd = new Map(
        (Array.isArray(depData) ? depData : []).map((d) => [
          NormalizeDepCdKey(d?.value),
          String(d?.label ?? '').trim(),
        ])
      );
      const list = selectedProcessCodes.map((item, index) => ({
        depCd: NormalizeDepCdKey(item?.depCd),
        depNm:
          String(item?.depNm ?? '').trim() ||
          depLabelByCd.get(NormalizeDepCdKey(item?.depCd)) ||
          '',
        processId: String(item.gongJongCd ?? item.value ?? `p-${index}`),
        processNm: GetProcessDisplayName(item),
        processNo: index + 1,
        selected: index === 0,
      }));
      const depNmJoined = [
        ...new Set(
          list
            .map((p) => String(p.depNm ?? '').trim())
            .filter((s) => s !== '')
        ),
      ].join(', ');
      return {
        ...prev,
        processList: list,
        depNm: depNmJoined || prev.depNm || '',
      };
    });
    setProcessSelectModalOpen(false);
    setSelectedProcessCodes([]);
    setSelectedDepartments([]);
    setDepartmentSearchText('');
    setProcessSearchText('');
  };

  const HandleOpenProcessRegisterModal = () => {
    if (selectedDepartments.length === 0) {
      dispatch(
        showSnackbar({
          message: '공정을 등록하려면 왼쪽에서 부서를 선택해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (selectedDepartments.length > 1) {
      dispatch(
        showSnackbar({
          message:
            '공정 등록은 왼쪽에서 부서를 하나만 선택한 상태에서 [공정 등록]을 눌러 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    const dep = selectedDepartments[0];
    setProcessRegisterData({
      depCd: NormalizeDepCdKey(dep.value),
      gongJongNm: '',
    });
    setProcessRegisterModalOpen(true);
  };

  const HandleCloseProcessRegisterModal = () => {
    setProcessRegisterModalOpen(false);
  };

  const HandleOpenFacilitySelectModal = () => {
    const pl = Array.isArray(form.processList) ? form.processList : [];
    const sel = pl.find((p) => p.selected) || pl[0];
    const equipmentList = Array.isArray(sel?.equipmentList)
      ? sel.equipmentList
      : Array.isArray(form.equipmentList)
        ? form.equipmentList
        : [];
    const asMachines = equipmentList.map((e) => {
      const mtRaw =
        e.machineType != null ? String(e.machineType).trim() : '0';
      const isManualOther = mtRaw === '4' || mtRaw === '04';
      return {
        machineId: e.id,
        machineNm: e.name,
        machineName: e.name,
        chaebun: e.id,
        machineType: isManualOther ? '4' : mtRaw,
        ...(isManualOther
          ? { categoryCd: FACILITY_OTHER_CATEGORY_VALUE }
          : {}),
      };
    });
    facilitySelection.loadFromSavedData(
      asMachines,
      SAFETY_INFO_FACILITY_CATEGORIES_WITH_OTHER,
      facilityDataByCategory
    );
    setItemSelectModalType('facility');
    setItemSelectModalOpen(true);
  };

  const HandleOpenMaterialSelectModal = () => {
    materialModalHydratedRef.current = false;
    setItemSelectModalType('material');
    setItemSelectModalOpen(true);
  };

  const HandleCloseItemSelectModal = () => {
    setItemSelectModalOpen(false);
  };

  const HandleConfirmItemSelection = (selectedItems) => {
    setForm((prev) => {
      const pl = Array.isArray(prev.processList) ? [...prev.processList] : [];
      let idx = pl.findIndex((p) => p.selected);
      if (idx < 0) idx = 0;

      if (itemSelectModalType === 'facility') {
        const list = (selectedItems || []).map((item) => ({
          id: String(item.chaebun || item.facilityCd || item.machineId || ''),
          name: String(item.machineName || item.machineNm || item.facilityNm || ''),
          machineType: String(
            item.categoryCd === FACILITY_OTHER_CATEGORY_VALUE
              ? item.machineType ?? '4'
              : item.machineType ?? item.categoryCd ?? '0'
          ),
        }));
        if (pl.length) {
          pl[idx] = { ...pl[idx], equipmentList: list };
        }
        return { ...prev, processList: pl, equipmentList: list };
      }

      const list = (selectedItems || []).map((item) => ({
        id: String(item.hbdId || item.materialId || ''),
        name: String(
          item.substanceName || item.materialName || item.materialNm || ''
        ),
        casNo: String(item.casNo || item.CAS_NO || ''),
        hbmId:
          Number(item.hbmId || item.productId) > 0
            ? Number(item.hbmId || item.productId)
            : MANUAL_MATERIAL_PRODUCT_ID,
        productName: item.productName || '',
      }));
      if (pl.length) {
        pl[idx] = { ...pl[idx], materialList: list };
      }
      return { ...prev, processList: pl, materialList: list };
    });
    setItemSelectModalOpen(false);
  };

  /**
   * SaveRisCustom01U3 / DeleteRisCustom01D3 등 BuildBatchResponse
   * (data·Data 배열 또는 결과 배열 직접) 및 단일 SaveRisBasicResult 대응
   */
  const IsSaveFailed = (result) => {
    if (!result) return true;
    if (result.IsSuccess === false || result.isSuccess === false) return true;

    const batch = result.data ?? result.Data;
    if (Array.isArray(batch) && batch.length > 0) {
      if (
        batch.some((r) => r?.isSuccess === false || r?.IsSuccess === false)
      ) {
        return true;
      }
    }

    if (
      Array.isArray(result) &&
      result.length > 0 &&
      result[0] &&
      typeof result[0] === 'object' &&
      ('IsSuccess' in result[0] || 'isSuccess' in result[0])
    ) {
      if (
        result.some((r) => r?.isSuccess === false || r?.IsSuccess === false)
      ) {
        return true;
      }
    }

    return false;
  };

  const PickRisBatchOrRootMessage = (result, fallback) => {
    const root = result?.Message ?? result?.message;
    if (root) return root;
    const batch = result?.data ?? result?.Data;
    if (Array.isArray(batch) && batch.length > 0) {
      const bad = batch.find(
        (r) => r?.isSuccess === false || r?.IsSuccess === false
      );
      const m = bad?.Message ?? bad?.message;
      if (m) return m;
    }
    if (
      Array.isArray(result) &&
      result[0] &&
      typeof result[0] === 'object' &&
      ('IsSuccess' in result[0] || 'isSuccess' in result[0])
    ) {
      const bad = result.find(
        (r) => r?.isSuccess === false || r?.IsSuccess === false
      );
      const m = bad?.Message ?? bad?.message;
      if (m) return m;
    }
    return fallback;
  };

  const SaveManualFacilityLogic = useCallback(
    async (machineName, options = {}) => {
      const { chaebun } = options;
      const trimmed = String(machineName ?? '').trim();
      if (!trimmed) return false;
      if (!searchInfo?.custCd) {
        dispatch(
          showSnackbar({
            message: '사업장을 선택한 뒤 설비를 등록해 주세요.',
            type: 'error',
          })
        );
        return false;
      }
      const isUpdate =
        chaebun != null && String(chaebun).trim() !== '';
      const row = isUpdate
        ? { machineName: trimmed, chaebun: String(chaebun).trim() }
        : { machineName: trimmed };
      try {
        const result = await SaveManualFacilityAsync([row]);
        if (IsSaveFailed(result)) {
          dispatch(
            showSnackbar({
              message: PickRisBatchOrRootMessage(
                result,
                isUpdate
                  ? '설비 수정에 실패했습니다.'
                  : '설비 등록에 실패했습니다.'
              ),
              type: 'error',
            })
          );
          return false;
        }
        dispatch(
          showSnackbar({
            message: isUpdate
              ? '설비 정보가 수정되었습니다.'
              : '설비가 등록되었습니다.',
            type: 'success',
          })
        );
        Search3();
        return true;
      } catch (e) {
        dispatch(
          showSnackbar({
            message:
              e?.message ??
              (isUpdate
                ? '설비 수정 중 오류가 발생했습니다.'
                : '설비 등록 중 오류가 발생했습니다.'),
            type: 'error',
          })
        );
        return false;
      }
    },
    [dispatch, searchInfo?.custCd, SaveManualFacilityAsync, Search3]
  );

  const DeleteManualFacilityLogic = useCallback(
    async (chaebun) => {
      const id = String(chaebun ?? '').trim();
      if (!id) {
        dispatch(
          showSnackbar({
            message: '삭제할 설비를 확인할 수 없습니다.',
            type: 'error',
          })
        );
        return false;
      }
      if (!searchInfo?.custCd) {
        dispatch(
          showSnackbar({
            message: '사업장을 선택한 뒤 다시 시도해 주세요.',
            type: 'error',
          })
        );
        return false;
      }
      try {
        const result = await DeleteManualFacilityAsync([{ chaebun: id }]);
        if (IsSaveFailed(result)) {
          dispatch(
            showSnackbar({
              message: PickRisBatchOrRootMessage(
                result,
                '설비 삭제에 실패했습니다.'
              ),
              type: 'error',
            })
          );
          return false;
        }
        dispatch(
          showSnackbar({
            message: '설비가 삭제되었습니다.',
            type: 'success',
          })
        );
        Search3();
        return true;
      } catch (e) {
        dispatch(
          showSnackbar({
            message: e?.message ?? '설비 삭제 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        return false;
      }
    },
    [dispatch, searchInfo?.custCd, DeleteManualFacilityAsync, Search3]
  );

  const SaveManualMaterialLogic = useCallback(
    async (substanceName, options = {}) => {
      const { hbdId, productName, casNo } = options;
      const trimmed = String(substanceName ?? '').trim();
      if (!trimmed) return false;
      if (!searchInfo?.custCd) {
        dispatch(
          showSnackbar({
            message: '사업장을 선택한 뒤 물질을 등록해 주세요.',
            type: 'error',
          })
        );
        return false;
      }
      const isUpdate = hbdId != null && String(hbdId).trim() !== '';
      const row = isUpdate
        ? {
            substanceName: trimmed,
            hbdId: String(hbdId).trim(),
            productName: String(productName || '수기 등록 물질'),
            casNo: String(casNo || ''),
          }
        : { substanceName: trimmed, productName: '수기 등록 물질' };
      try {
        const result = await SaveManualMaterialAsync([row]);
        if (IsSaveFailed(result)) {
          dispatch(
            showSnackbar({
              message: PickRisBatchOrRootMessage(
                result,
                isUpdate
                  ? '물질 수정에 실패했습니다.'
                  : '물질 등록에 실패했습니다.'
              ),
              type: 'error',
            })
          );
          return false;
        }
        dispatch(
          showSnackbar({
            message: isUpdate ? '물질 정보가 수정되었습니다.' : '물질이 등록되었습니다.',
            type: 'success',
          })
        );
        Search3();
        return true;
      } catch (e) {
        dispatch(
          showSnackbar({
            message:
              e?.message ??
              (isUpdate
                ? '물질 수정 중 오류가 발생했습니다.'
                : '물질 등록 중 오류가 발생했습니다.'),
            type: 'error',
          })
        );
        return false;
      }
    },
    [dispatch, searchInfo?.custCd, SaveManualMaterialAsync, Search3]
  );

  const DeleteManualMaterialLogic = useCallback(
    async (hbdId) => {
      const id = String(hbdId ?? '').trim();
      if (!id) {
        dispatch(
          showSnackbar({
            message: '삭제할 물질을 확인할 수 없습니다.',
            type: 'error',
          })
        );
        return false;
      }
      if (!searchInfo?.custCd) {
        dispatch(
          showSnackbar({
            message: '사업장을 선택한 뒤 다시 시도해 주세요.',
            type: 'error',
          })
        );
        return false;
      }
      try {
        const result = await DeleteManualMaterialAsync([{ hbdId: id }]);
        if (IsSaveFailed(result)) {
          dispatch(
            showSnackbar({
              message: PickRisBatchOrRootMessage(
                result,
                '물질 삭제에 실패했습니다.'
              ),
              type: 'error',
            })
          );
          return false;
        }
        dispatch(
          showSnackbar({
            message: '물질이 삭제되었습니다.',
            type: 'success',
          })
        );
        Search3();
        return true;
      } catch (e) {
        dispatch(
          showSnackbar({
            message: e?.message ?? '물질 삭제 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
        return false;
      }
    },
    [dispatch, searchInfo?.custCd, DeleteManualMaterialAsync, Search3]
  );

  const SaveSafetyInfoByStepSeq = async ({ stepSeq, successMessage, afterSuccess }) => {
    const result = await SaveSafetyInfoAsync([
      {
        ...form,
        rsCd: Number(assessmentRsCd) || Number(form.rsCd) || 0,
        stepSeq,
      },
    ]);
    if (IsSaveFailed(result)) {
      dispatch(
        showSnackbar({
          message: result?.Message ?? result?.message ?? '저장에 실패했습니다.',
          type: 'error',
        })
      );
      return;
    }
    dispatch(
      showSnackbar({
        message: successMessage,
        type: 'success',
      })
    );
    afterSuccess?.();
  };

  const HandleSaveLogic1 = async () => {
    // 저장 후 다음: 다음 단계로 상태 진행 (StepSeq=2)
    await SaveSafetyInfoByStepSeq({
      stepSeq: 2,
      successMessage: '저장되었습니다.',
      afterSuccess: () => onSaved?.(),
    });
  };

  const riskListColumnDefs = useMemo(
    () => [
      { field: 'rsNm', headerName: '위험성평가명', flex: 1.4, minWidth: 220 },
      { field: 'rsGubun', headerName: '평가구분', flex: 0.8, minWidth: 120 },
      { field: 'rsMethod', headerName: '평가방법', flex: 1.1, minWidth: 180 },
      { field: 'depNm', headerName: '부서', flex: 0.9, minWidth: 140 },
      { field: 'rsStartDate', headerName: '평가 시작일', flex: 0.8, minWidth: 130 },
      { field: 'rsEndDate', headerName: '평가 종료일', flex: 0.8, minWidth: 130 },
      { field: 'rsMember', headerName: '평가자', flex: 0.8, minWidth: 120 },
    ],
    []
  );

  const MapRiskMasterRows = (raw) => {
    const first = raw?.Data?.[0] ?? raw?.data?.[0] ?? {};
    const list =
      first?.RiskMasterList ??
      first?.riskMasterList ??
      raw?.RiskMasterList ??
      raw?.riskMasterList ??
      [];
    return (Array.isArray(list) ? list : []).map((item, index) => ({
      no: index + 1,
      rsCd: item.RsCd ?? item.rsCd,
      rsNm: item.RsNm ?? item.rsNm ?? '',
      rsGubun: item.RsGubun ?? item.rsGubun ?? '',
      rsMethod: item.RsMethod ?? item.rsMethod ?? '',
      depNm: item.DepNm ?? item.depNm ?? '',
      rsStartDate: String(item.RsStartDate ?? item.rsStartDate ?? '').slice(0, 10),
      rsEndDate: String(item.RsEndDate ?? item.rsEndDate ?? '').slice(0, 10),
      rsMember:
        item.RsMemberNm ?? item.rsMemberNm ?? item.rsMember ?? item.RsMember ?? '',
    }));
  };

  const LoadRiskListRows = async () => {
    try {
      const params = mapParamsBySchema(RIS_CUSTOM_Schema.mapper.search1, {
        search: {
          custCd: searchInfo?.custCd || '',
          startDate: searchInfo?.startDate || '',
          searchText: '',
        },
      });
      const raw = await apiRequest('post', Global.urls.RiskEvaluation.getRisCustomR1, params);
      setRiskListRows(MapRiskMasterRows(raw));
    } catch {
      setRiskListRows([]);
    }
  };

  const ApplyExistingAssessment = async (row) => {
    const rsCd = Number(row?.rsCd);
    if (!Number.isFinite(rsCd) || rsCd <= 0) return;
    try {
      const params = mapParamsBySchema(SafetyInfo_Schema.mapper.search1, {
        rsCd,
        searchText: '',
      });
      const raw = await apiRequest(
        'post',
        Global.urls.RiskEvaluation.getRisCustom01R4,
        params
      );
      const patch = MapGetRisCustom01R4ResponseToForm(raw, {
        assessmentTitle: row?.rsNm || assessmentTitle,
      });
      if (!patch) return;

      const detailFlat = ExtractGetRisCustom01R4RiskDetailList(raw);
      const risk02 = MapGetRisCustom02R1ToProcesses({ data: detailFlat });
      const sourcePl = Array.isArray(patch.processList) ? patch.processList : [];
      const mergedForOverlay = MergeSafetyProcessListWithRiskDetails(
        sourcePl,
        risk02
      );
      const byGongJongCd = {};
      for (const p of mergedForOverlay) {
        const k = String(p.gongJongCd ?? '').trim();
        if (!k) continue;
        byGongJongCd[k] = StripCopiedRiskDetailRowsForDraft(
          Array.isArray(p.details) ? p.details : []
        );
      }
      const targetRs = Number(assessmentRsCd);
      if (risWorkflow?.setRiskDetailOverlay && Number.isFinite(targetRs) && targetRs > 0) {
        risWorkflow.setRiskDetailOverlay({
          rsCd: targetRs,
          byGongJongCd,
        });
      }

      // "기존 위험성평가 불러오기"는 복사 개념이므로 원본 SRI_ID를 재사용하면 안 됨.
      // SRI_ID를 0으로 초기화해 SaveRisCustom01U2에서 현재 평가(RS_CD)에 INSERT 되도록 보정.
      const copiedProcessList = sourcePl.map((p, idx) => ({
        ...p,
        sriId: 0,
        processNo: Number(p?.processNo ?? idx + 1),
        selected: idx === 0,
        equipmentList: (Array.isArray(p?.equipmentList) ? p.equipmentList : []).map((m) => ({
          ...m,
          sriId: null,
          mapId: null,
        })),
        materialList: (Array.isArray(p?.materialList) ? p.materialList : []).map((m) => ({
          ...m,
          sriId: null,
          mapId: null,
        })),
      }));
      setForm((prev) => ({
        ...prev,
        ...patch,
        // 현재 평가를 수정 중이므로 RS_CD는 assessmentRsCd를 유지해야 함.
        rsCd: Number(assessmentRsCd) || Number(prev.rsCd) || 0,
        processList: copiedProcessList,
        equipmentList: copiedProcessList[0]?.equipmentList ?? [],
        materialList: copiedProcessList[0]?.materialList ?? [],
        rsNm: prev.rsNm || row?.rsNm || patch.rsNm,
        rsStartDate: patch.rsStartDate || prev.rsStartDate,
        rsEndDate: patch.rsEndDate || prev.rsEndDate,
      }));
      dispatch(showSnackbar({ message: '기존 위험성평가를 불러왔습니다.', type: 'success' }));
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '기존 위험성평가 불러오기에 실패했습니다.',
          type: 'error',
        })
      );
    }
  };

  const HandleLoadExisting = async () => {
    await LoadRiskListRows();
    setRiskListSearchText('');
    setRiskListModalOpen(true);
  };

  const HandleTempSave = async () => {
    // 임시저장: 현재 단계 유지 (StepSeq=1)
    await SaveSafetyInfoByStepSeq({
      stepSeq: 1,
      successMessage: '임시 저장되었습니다.',
    });
  };

  return (
    <>
      <SafetyInfo
        value={form}
        workflowSteps={workflowStepsFromApi}
        onChange={HandleChange}
        onSave={HandleSaveLogic1}
        onCancel={onCancel}
        onLoadExisting={HandleLoadExisting}
        onTempSave={HandleTempSave}
        onOpenProcessSelectModal={HandleOpenProcessSelectModal}
        onOpenFacilitySelectModal={HandleOpenFacilitySelectModal}
        onOpenMaterialSelectModal={HandleOpenMaterialSelectModal}
      />
      <RiskAssessmentListModal
        open={riskListModalOpen}
        onClose={() => setRiskListModalOpen(false)}
        onSelect={ApplyExistingAssessment}
        onDoubleClick={ApplyExistingAssessment}
        rowData={riskListRows}
        columnDefs={riskListColumnDefs}
        gridRef={riskListGridRef}
        searchText={riskListSearchText}
        setSearchText={setRiskListSearchText}
      />
      <ProcessSelectModal
        open={processSelectModalOpen}
        onClose={HandleCloseProcessSelectModal}
        onConfirm={HandleConfirmProcessSelection}
        depData={depData}
        processDataByDept={processDataByDept}
        selectedDepartments={selectedDepartments}
        onToggleDepartment={HandleToggleDepartment}
        selectedProcessCodes={selectedProcessCodes}
        onToggleProcessCode={HandleToggleProcessCode}
        onDragEnd={handleDragEndProcess}
        onClearAll={HandleClearAllProcesses}
        onOpenRegisterModal={HandleOpenProcessRegisterModal}
        departmentSearchText={departmentSearchText}
        setDepartmentSearchText={setDepartmentSearchText}
        processSearchText={processSearchText}
        setProcessSearchText={setProcessSearchText}
      />
      <ProcessRegisterModal
        open={processRegisterModalOpen}
        onClose={HandleCloseProcessRegisterModal}
        processRegisterData={processRegisterData}
        setProcessRegisterData={setProcessRegisterData}
        registerTargetDepLabel={
          selectedDepartments.length === 1
            ? String(selectedDepartments[0]?.label ?? '').trim()
            : ''
        }
        SaveLogic2={SaveProcessRegister}
      />
      <ItemSelectModal
        open={itemSelectModalOpen}
        onClose={HandleCloseItemSelectModal}
        onConfirm={HandleConfirmItemSelection}
        onSaveManualFacility={SaveManualFacilityLogic}
        onDeleteManualFacility={DeleteManualFacilityLogic}
        onSaveManualMaterial={SaveManualMaterialLogic}
        onDeleteManualMaterial={DeleteManualMaterialLogic}
        modalConfig={
          itemSelectModalType === 'facility'
            ? facilitySelection.getModalConfig()
            : materialSelection.getModalConfig()
        }
        modalType={itemSelectModalType}
      />
    </>
  );
}

export default SafetyInfoF;
